import React, { useState, useEffect } from 'react';
import { IconButton, Typography, Button, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import ToastStr from '../../../request/toastStr';
import { COLORS } from '../../../shared/Theme';
import { member as v_member, basic, Values } from '../../../request/values';

const SERVER = basic.server;

const useStyles = makeStyles((theme) => ({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		minHeight: '100vh',
		padding: '0',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '16px 20px',
		position: 'relative',
		marginTop: '2vh',
		maxWidth: '550px',
		width: '100%', // 추가: 최소한 부모의 100%를 차지하도록
		boxSizing: 'border-box', // 패딩 포함 크기 계산
	},
	image: {
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		'& img': {
			width: '50vw',
			height: 'auto',
			maxWidth: '250px',
		},
	},
	content: {
		flex: 1,
		padding: '40px 20px 0',
		display: 'flex',
		flexDirection: 'column',
		maxWidth: '550px',
		width: '100%', // 추가: 부모의 100%를 차지하도록
		boxSizing: 'border-box',
	},
	title: {
		fontSize: '24px',
		color: COLORS.WATER_BLUE,
		fontWeight: 600,
		marginBottom: '20px',
	},
	inputContainer: {
		marginBottom: '20px',
	},
	nameInput: {
		width: '100%',
		'& .MuiOutlinedInput-root': {
			borderRadius: '8px',
			backgroundColor: 'white',
			'& .MuiOutlinedInput-notchedOutline': {
				borderColor: '#e1e5e9',
				transition: 'border-color 50ms ease',
			},
			'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
				borderColor: '#3f51b5',
			},
		},
		'& .MuiOutlinedInput-input': {
			padding: '18px 20px',
			fontSize: '14px',
			lineHeight: '24px',
		},
	},
	footer: {
		position: 'fixed',
		left: 0,
		right: 0,
		bottom: 0,
		background: '#fff',
		borderTop: '1px solid #eee',
		display: 'flex',
		justifyContent: 'center',
		padding: 20,
	},
	submit: {
		width: '100%',
		maxWidth: 550,
		height: 48,
		borderRadius: 10,
		fontWeight: 700,
		color: 'white',
		backgroundColor: COLORS.WATER_BLUE,
		'&:hover': {
			backgroundColor: '#137ab4',
		},
	},
}));

const NameInput: React.FC<any> = (props) => {
	const classes = useStyles();
	const { t } = useTranslation('student');
	const [name, setName] = useState<string>('');
	const [submitLoading, setSubmitLoading] = useState<boolean>(false);
	const [initLoading, setInitLoading] = useState<boolean>(true);
	let history = useHistory();
	const { enqueueSnackbar } = useSnackbar();

	const { member, setMember } = props;
	const isRealNameMode = Number(member?.classVersion) === 5;

	const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		if (value.length <= 12) {
			setName(value);
		}
	};

	const submitName = async () => {
		const trimmedName = name.trim();
		if (trimmedName.length === 0) {
			enqueueSnackbar(isRealNameMode ? t('NameInput.requiredName') : t('NameInput.required'), { variant: 'warning' });
			return;
		}
		setSubmitLoading(true);
		try {
			const res = await axios({
				method: 'POST',
				data: {
					name: trimmedName,
					userId: member.id,
				},
				withCredentials: true,
				url: SERVER + v_member.member + v_member.name_upd,
			});
			const data = await res.data;
			if (data.code == Values.SUCCESS_CODE) {
				setMember(Object.assign({}, member, { name: trimmedName }));
				history.push({
					pathname: '/CompanyProcess',
				});
			}
		} catch (error) {
			enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, { variant: 'error' });
			setSubmitLoading(false);
		}
	};

	const getMemberName = async (signal: AbortSignal) => {
		let url = new URL(SERVER + v_member.member + v_member.name_upd);
		let params = { userId: member.id };
		url.search = new URLSearchParams(params).toString();

		try {
			const res = await axios({
				method: 'GET',
				withCredentials: true,
				url: url.toString(),
				signal: signal,
			});
			const data = await res.data;
			if (data.code == Values.SUCCESS_CODE) {
				if (data.name) {
					setName(data.name);
				}
			}
		} catch (error) {
			//console.error('getMemberName error', error);
			return;
		} finally {
			if (!signal.aborted) {
				setInitLoading(false);
			}
		}
	};

	useEffect(() => {
		const controller = new AbortController();
		getMemberName(controller.signal);
		return () => controller.abort();
	}, []);

	return (
		<div className={classes.container}>
			<div className={classes.header}>
				<div className={classes.image}>
					<img src="/images/playstartup_color.webp" />
				</div>
			</div>

			<div className={classes.content}>
				<Typography variant="h4" className={classes.title}>
					{isRealNameMode ? t('NameInput.titleName') : t('NameInput.title')}
				</Typography>

				<div className={classes.inputContainer}>
					<TextField
						className={classes.nameInput}
						variant="outlined"
						placeholder={isRealNameMode ? t('NameInput.placeholderName') : t('NameInput.placeholder')}
						value={name}
						onChange={handleNameChange}
						inputProps={{
							maxLength: 12,
							'data-testid': 'set-name-input',
						}}
					/>
				</div>

				<div className={classes.footer}>
					<Button className={classes.submit} data-testid="set-name-confirm-btn" variant="contained" disabled={name.trim().length === 0 || submitLoading || initLoading} onClick={submitName}>
						{submitLoading || initLoading ? t('NameInput.saving') : t('NameInput.save')}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default NameInput;
